import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Stack,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ORGANIZATION_ROLES from "../../../shared/constants/organizationRoles";
import ORGANIZATION_TYPES from "../../../shared/constants/organizationTypes";
import { ProfileDatePicker } from './ProfileDialogs';
import { useUserContext } from 'shared/contexts/UserContext';

/**
 * AddAffiliationDialog — unified dialog for adding an organization affiliation.
 *
 * Props:
 *   open, onClose          — dialog visibility
 *   countries              — array of { code, name, alpha_2 } from consortium Country
 *   allOrganizations       — array of org objects (pre-fetched by parent)
 *   loadingAllOrgs         — boolean
 *   fetchOrgDepartments    — (orgId) => void
 *   orgDepartments         — array of department objects
 *   loadingDepartments     — boolean
 *   onSubmit               — (membershipData) => Promise<void>
 *   submitting             — boolean
 */
const AddAffiliationDialog = ({
  open,
  onClose,
  countries = [],
  allOrganizations = [],
  loadingAllOrgs = false,
  fetchOrgDepartments,
  orgDepartments = [],
  loadingDepartments = false,
  onSubmit,
  submitting = false,
  isFirstAffiliation = false,
}) => {
  const { user } = useUserContext();
  const userCountryCode = user?.country?.code || '';

  const [creatingNewOrg, setCreatingNewOrg] = useState(false);

  const [affiliationForm, setAffiliationForm] = useState({
    organization: null,
    department: null,
    role: 'Researcher',
    start_date: new Date().toISOString().split('T')[0],
    is_primary: false,
    status: 'active',
  });

  const [newOrgForm, setNewOrgForm] = useState({
    name: '',
    org_type: '',
    website: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state_region: '',
      postal_code: '',
      country_code: userCountryCode,
    },
  });

  // Reset on open
  useEffect(() => {
    if (open) {
      setCreatingNewOrg(false);
      setAffiliationForm({
        organization: null,
        department: null,
        role: 'Researcher',
        start_date: new Date().toISOString().split('T')[0],
        is_primary: isFirstAffiliation,
        status: 'active',
      });
      setNewOrgForm({
        name: '',
        org_type: '',
        website: '',
        address: { line1: '', line2: '', city: '', state_region: '', postal_code: '', country_code: userCountryCode },
      });
    }
  }, [open, isFirstAffiliation, userCountryCode]);

  // Check if the new org name already exists (case-insensitive)
  const orgNameExists = creatingNewOrg && newOrgForm.name.trim().length > 0 &&
    allOrganizations.some(
      (org) => org.name?.toLowerCase() === newOrgForm.name.trim().toLowerCase()
    );

  // Validation
  const isDisabled = useCallback(() => {
    if (creatingNewOrg) {
      if (!newOrgForm.name.trim()) return true;
      if (orgNameExists) return true;
      if (!newOrgForm.website.trim()) return true;
      if (!newOrgForm.address.line1.trim()) return true;
      if (!newOrgForm.address.city.trim()) return true;
    } else {
      if (!affiliationForm.organization) return true;
    }
    if (!affiliationForm.role.trim()) return true;
    if (!affiliationForm.start_date) return true;
    return false;
  }, [creatingNewOrg, newOrgForm, affiliationForm, orgNameExists]);

  const handleSubmit = () => {
    const membershipData = {
      role: affiliationForm.role,
      start_date: affiliationForm.start_date,
      is_primary: affiliationForm.is_primary,
      status: affiliationForm.status,
    };

    if (creatingNewOrg) {
      membershipData.new_organization = {
        name: newOrgForm.name,
        org_type: newOrgForm.org_type || 'Other',
        website: newOrgForm.website,
        address: newOrgForm.address,
      };
      if (affiliationForm.department && typeof affiliationForm.department === 'string' && affiliationForm.department.trim()) {
        membershipData.department_name = affiliationForm.department.trim();
      }
    } else {
      membershipData.org = affiliationForm.organization.id;
      if (affiliationForm.department) {
        if (typeof affiliationForm.department === 'object' && affiliationForm.department.id) {
          membershipData.department = affiliationForm.department.id;
        } else if (typeof affiliationForm.department === 'string' && affiliationForm.department.trim()) {
          membershipData.department_name = affiliationForm.department.trim();
        }
      }
    }

    onSubmit(membershipData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {creatingNewOrg ? 'Create Organization & Add Affiliation' : 'Add Organization Affiliation'}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Toggle existing / new */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant={!creatingNewOrg ? "contained" : "outlined"}
              onClick={() => {
                setCreatingNewOrg(false);
                setAffiliationForm(prev => ({ ...prev, department: null }));
              }}
              fullWidth
            >
              Select Existing
            </Button>
            <Button
              variant={creatingNewOrg ? "contained" : "outlined"}
              onClick={() => {
                setCreatingNewOrg(true);
                setAffiliationForm(prev => ({ ...prev, department: null }));
              }}
              fullWidth
            >
              Create New Organization
            </Button>
          </Box>

          {/* ---- Existing Organization ---- */}
          {!creatingNewOrg ? (
            <Autocomplete
              options={allOrganizations}
              getOptionLabel={(option) => option.name || ''}
              value={affiliationForm.organization}
              onChange={(_, newValue) => {
                setAffiliationForm(prev => ({ ...prev, organization: newValue, department: null }));
                if (newValue && fetchOrgDepartments) {
                  fetchOrgDepartments(newValue.id);
                }
              }}
              loading={loadingAllOrgs}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Organization"
                  placeholder="Search or select an organization"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingAllOrgs ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : (
            /* ---- New Organization Form ---- */
            <>
              <TextField
                label="Organization Name"
                value={newOrgForm.name}
                onChange={(e) => setNewOrgForm(prev => ({ ...prev, name: e.target.value }))}
                required
                fullWidth
                placeholder="e.g., University of Example"
                error={creatingNewOrg && (!newOrgForm.name.trim() || orgNameExists)}
                helperText={
                  creatingNewOrg && orgNameExists
                    ? 'An organization with this name already exists. Please select it from the existing organizations list.'
                    : creatingNewOrg && !newOrgForm.name.trim()
                      ? 'Organization name is required'
                      : ''
                }
              />

              <TextField
                label="Website *"
                value={newOrgForm.website}
                onChange={(e) => setNewOrgForm(prev => ({ ...prev, website: e.target.value }))}
                required
                fullWidth
                placeholder="https://example.com"
                type="url"
                error={creatingNewOrg && !newOrgForm.website.trim()}
                helperText={creatingNewOrg && !newOrgForm.website.trim() ? "Required for organization verification" : ""}
              />

              <Autocomplete
                freeSolo
                options={ORGANIZATION_TYPES}
                value={newOrgForm.org_type}
                onChange={(_, newValue) => setNewOrgForm(prev => ({ ...prev, org_type: newValue || '' }))}
                onInputChange={(_, v) => setNewOrgForm(prev => ({ ...prev, org_type: v || '' }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization Type"
                    placeholder="Select or type organization type"
                    helperText="Select from list or type a custom type"
                  />
                )}
              />

              <Typography variant="subtitle2" sx={{ mt: 2 }}>Organization Address</Typography>

              <TextField
                label="Address Line 1"
                value={newOrgForm.address.line1}
                onChange={(e) => setNewOrgForm(prev => ({ ...prev, address: { ...prev.address, line1: e.target.value } }))}
                required
                fullWidth
                placeholder="Street address"
              />

              <TextField
                label="Address Line 2 (Optional)"
                value={newOrgForm.address.line2}
                onChange={(e) => setNewOrgForm(prev => ({ ...prev, address: { ...prev.address, line2: e.target.value } }))}
                fullWidth
                placeholder="Apartment, suite, etc."
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="City"
                  value={newOrgForm.address.city}
                  onChange={(e) => setNewOrgForm(prev => ({ ...prev, address: { ...prev.address, city: e.target.value } }))}
                  required
                  fullWidth
                />
                <TextField
                  label="State/Region"
                  value={newOrgForm.address.state_region}
                  onChange={(e) => setNewOrgForm(prev => ({ ...prev, address: { ...prev.address, state_region: e.target.value } }))}
                  fullWidth
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Postal Code"
                  value={newOrgForm.address.postal_code}
                  onChange={(e) => setNewOrgForm(prev => ({ ...prev, address: { ...prev.address, postal_code: e.target.value } }))}
                  fullWidth
                />
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return `${option.name} (${option.code})`;
                  }}
                  value={countries.find(c => c.code === newOrgForm.address.country_code) || null}
                  onChange={(_, newValue) => {
                    setNewOrgForm(prev => ({ ...prev, address: { ...prev.address, country_code: newValue?.code || '' } }));
                  }}
                  isOptionEqualToValue={(option, value) => option.code === value?.code}
                  fullWidth
                  renderInput={(params) => (
                    <TextField {...params} label="Country" placeholder="Search for a country" />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.code}>
                      {option.name} ({option.code})
                    </li>
                  )}
                />
              </Box>
            </>
          )}

          {/* Department — for existing org: autocomplete; for new org: text input */}
          {!creatingNewOrg && affiliationForm.organization ? (
            <Autocomplete
              options={orgDepartments}
              freeSolo
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option?.name || '';
              }}
              value={affiliationForm.department}
              onChange={(_, newValue) => setAffiliationForm(prev => ({ ...prev, department: newValue }))}
              onInputChange={(_, v) => {
                if (v && typeof v === 'string') {
                  setAffiliationForm(prev => ({ ...prev, department: v }));
                }
              }}
              disabled={!affiliationForm.organization || loadingDepartments}
              loading={loadingDepartments}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department (Optional)"
                  placeholder="Select or type a department name"
                  helperText={!affiliationForm.organization ? "Select an organization first" : "Type to create a new department"}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDepartments ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          ) : creatingNewOrg ? (
            <TextField
              label="Department Name (Optional)"
              value={affiliationForm.department || ''}
              onChange={(e) => setAffiliationForm(prev => ({ ...prev, department: e.target.value }))}
              fullWidth
              placeholder="e.g., Computer Science"
              helperText="Leave empty or type a department name to create one"
            />
          ) : null}

          {/* Role */}
          <Autocomplete
            freeSolo
            options={ORGANIZATION_ROLES}
            value={affiliationForm.role}
            onChange={(_, newValue) => setAffiliationForm(prev => ({ ...prev, role: newValue || '' }))}
            onInputChange={(_, v) => setAffiliationForm(prev => ({ ...prev, role: v }))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Role/Position"
                placeholder="Select or type your role"
                required
                helperText="Select from list or type a custom role"
              />
            )}
          />

          {/* Start date */}
          <ProfileDatePicker
            label="Start Date *"
            value={affiliationForm.start_date}
            onChange={(val) => setAffiliationForm(prev => ({ ...prev, start_date: val }))}
            disableFuture
          />

          {/* Is primary */}
          <FormControlLabel
            control={
              <Checkbox
                checked={affiliationForm.is_primary}
                onChange={(e) => setAffiliationForm(prev => ({ ...prev, is_primary: e.target.checked }))}
              />
            }
            label={<Typography variant="body2">Set as primary organization</Typography>}
          />

          <Alert severity="info">
            {creatingNewOrg
              ? "Creating a new organization will make it available for other users to select. You'll be automatically affiliated with it."
              : "Adding an affiliation will link you to this organization and make it visible in your profile."}
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || isDisabled()}
          startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {creatingNewOrg ? 'Create & Add Affiliation' : 'Add Affiliation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAffiliationDialog;